import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, ArrowLeft, Heart, Loader2, Lock, MessageCircle, Eye, Video, Star, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { track } from "../lib/analytics";

const COUNTRIES = [
  { iso: "SN", label: "Sénégal (+221)" },
  { iso: "CI", label: "Côte d'Ivoire (+225)" },
  { iso: "CM", label: "Cameroun (+237)" },
  { iso: "ML", label: "Mali (+223)" },
  { iso: "BF", label: "Burkina Faso (+226)" },
  { iso: "BJ", label: "Bénin (+229)" },
  { iso: "TG", label: "Togo (+228)" },
  { iso: "GN", label: "Guinée (+224)" },
  { iso: "GA", label: "Gabon (+241)" },
  { iso: "CG", label: "Congo (+242)" },
  { iso: "CD", label: "RD Congo (+243)" },
  { iso: "NE", label: "Niger (+227)" },
  { iso: "TD", label: "Tchad (+235)" },
  { iso: "MA", label: "Maroc (+212)" },
  { iso: "DZ", label: "Algérie (+213)" },
  { iso: "TN", label: "Tunisie (+216)" },
  { iso: "FR", label: "France (+33)" },
  { iso: "BE", label: "Belgique (+32)" },
  { iso: "CH", label: "Suisse (+41)" },
  { iso: "DE", label: "Allemagne (+49)" },
  { iso: "IT", label: "Italie (+39)" },
  { iso: "ES", label: "Espagne (+34)" },
  { iso: "GB", label: "Royaume-Uni (+44)" },
  { iso: "CA", label: "Canada (+1)" },
  { iso: "US", label: "États-Unis (+1)" },
];

const BENEFITS = [
  { icon: MessageCircle, text: "Messages illimités, Tchat direct avec les membres en ligne" },
  { icon: Eye, text: "Voyez qui vous a liké(e) et vos visiteurs en clair" },
  { icon: Video, text: "Appels vidéo avec vos matchs" },
  { icon: Star, text: "Super Likes, filtres avancés, mode invisible et badge Premium" },
];

const Premium = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [processing, setProcessing] = useState(false);
  const [phone, setPhone] = useState("");
  const [country, setCountry] = useState("SN");

  const startCheckout = async (e) => {
    e.preventDefault();
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 6) {
      toast.error("Entrez un numéro de téléphone valide.");
      return;
    }
    setProcessing(true);
    track("begin_checkout", { plan: "premium", currency: "USD", value: 9 });
    try {
      const { data } = await api.post("/payments/checkout", { phone_number: digits, phone_country: country });
      if (data.already_premium) {
        toast.success("Vous êtes déjà Premium !");
        navigate("/decouvrir");
        return;
      }
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error("Impossible d'ouvrir le paiement.");
        setProcessing(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Le paiement n'a pas pu démarrer.");
      setProcessing(false);
    }
  };

  if (user?.premium) {
    const until = user.premium_until ? new Date(user.premium_until) : null;
    const expiringSoon = until && until.getTime() - Date.now() <= 3 * 86400000;
    if (!expiringSoon) {
      return (
        <div className="min-h-screen bg-kora-sand flex items-center justify-center px-6">
          <div className="text-center max-w-sm">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-kora-terracotta to-kora-ochre flex items-center justify-center mx-auto mb-5">
              <Sparkles size={36} className="text-white" />
            </div>
            <h1 className="font-display font-black text-3xl text-kora-ink">Vous êtes Premium ✨</h1>
            <p className="text-kora-ink/60 mt-2">
              Votre pass KoraLove Premium est actif
              {until && (
                <> jusqu'au <span data-testid="premium-until-date" className="font-bold text-kora-ink">{until.toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}</span></>
              )}.
            </p>
            <button data-testid="premium-continue-btn" onClick={() => navigate("/decouvrir")} className="mt-6 w-full bg-kora-terracotta text-white rounded-full px-8 py-4 font-bold hover:bg-[#C94F2B] transition-colors">Continuer</button>
            <p className="text-xs text-kora-muted mt-4">Votre pass de 30 jours n'est pas renouvelé automatiquement. Vous pourrez le renouveler ici dès qu'il restera 3 jours ou moins.</p>
          </div>
        </div>
      );
    }
  }

  const renewUntil = user?.premium && user?.premium_until ? new Date(user.premium_until) : null;

  return (
    <div className="min-h-screen bg-kora-ink/60 backdrop-blur-sm flex items-center justify-center px-5 py-8">
      <div data-testid="premium-checkout-card" className="w-full max-w-md bg-kora-sand rounded-3xl shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-br from-kora-ink to-[#43342a] p-6 text-white text-center relative">
          <button data-testid="premium-back" onClick={() => navigate(-1)} className="absolute left-4 top-4 text-white/80 hover:text-white flex items-center gap-1 text-sm font-medium">
            <ArrowLeft size={18} /> Retour
          </button>
          <span className="inline-block bg-white/15 px-4 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider mt-4">KoraLove Premium</span>
          <div className="mt-3 flex items-baseline justify-center gap-2">
            <span className="text-white/50 line-through text-lg" data-testid="premium-old-price">16 $</span>
            <span className="font-display font-black text-5xl" data-testid="premium-price">9 $</span>
          </div>
          <p className="text-white/70 text-xs mt-1.5">Pass 1 mois - Paiement unique dans votre devise locale</p>
          {renewUntil && (
            <p data-testid="premium-renewal-note" className="mt-2 text-[11px] bg-white/15 rounded-full px-3 py-1 inline-block">
              🔄 Renouvellement : vos jours restants sont conservés — votre pass sera prolongé de 30 jours après le {renewUntil.toLocaleDateString("fr-FR", { day: "2-digit", month: "long" })}
            </p>
          )}
        </div>

        <div className="p-5">
          <div className="space-y-2.5">
            {BENEFITS.map((b, i) => (
              <div key={i} className="flex items-center gap-3 bg-white rounded-2xl px-4 py-3 border border-kora-line">
                <div className="w-8 h-8 rounded-lg bg-kora-terracotta/10 flex items-center justify-center shrink-0">
                  <b.icon size={16} className="text-kora-terracotta" />
                </div>
                <p className="text-[13px] font-medium text-kora-ink">{b.text}</p>
              </div>
            ))}
          </div>

          <form onSubmit={startCheckout} className="mt-5">
            <label className="text-[11px] font-bold uppercase tracking-wider text-kora-muted">Pays et numéro de téléphone</label>
            <div className="flex gap-2 mt-1.5">
              <div className="relative w-[45%] shrink-0">
                <select
                  data-testid="premium-phone-country"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className="w-full h-12 appearance-none pl-3 pr-8 rounded-xl border border-kora-line bg-white text-sm text-kora-ink truncate focus:outline-none focus:border-kora-terracotta"
                >
                  {COUNTRIES.map((c) => (
                    <option key={c.iso} value={c.iso}>{c.label}</option>
                  ))}
                </select>
                <ChevronDown size={16} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-kora-muted pointer-events-none" />
              </div>
              <input
                data-testid="premium-phone-input"
                type="tel"
                inputMode="numeric"
                placeholder="77 123 45 67"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="flex-1 min-w-0 h-12 px-3.5 rounded-xl border border-kora-line bg-white text-sm focus:outline-none focus:border-kora-terracotta"
                required
              />
            </div>

            <button
              data-testid="premium-subscribe-btn"
              type="submit"
              disabled={processing}
              className="w-full mt-4 bg-kora-terracotta text-white rounded-full py-4 font-bold hover:bg-[#C94F2B] transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:animate-none animate-kora-pulse"
            >
              {processing ? <Loader2 size={18} className="animate-spin" /> : <Heart size={18} className="fill-current" />}
              {processing ? "Redirection vers le paiement…" : "Payer maintenant — 9 $"}
            </button>
          </form>

          <div className="mt-4 flex flex-col items-center gap-2">
            <div className="flex items-center justify-center gap-2 flex-wrap">
              <span className="inline-flex items-center h-6 px-2 rounded-md bg-[#1DC3EF] text-white font-black text-[10px]">Wave</span>
              <span className="inline-flex items-center h-6 px-2 rounded-md bg-[#FF7900] text-white font-black text-[10px]">Orange Money</span>
              <span className="inline-flex items-center h-6 px-2 rounded-md bg-[#FFCB05] text-[#004F9F] font-black text-[10px]">MTN MoMo</span>
              <span className="inline-flex items-center h-6 px-2 rounded-md bg-white border border-kora-line font-display font-black italic text-[11px] tracking-tight text-[#1A1F71]">VISA</span>
              <span className="inline-flex items-center h-6 px-1.5 rounded-md bg-white border border-kora-line" aria-label="Mastercard">
                <span className="w-3.5 h-3.5 rounded-full bg-[#EB001B]" />
                <span className="w-3.5 h-3.5 rounded-full bg-[#F79E1B] -ml-1.5 mix-blend-multiply" />
              </span>
            </div>
            <div data-testid="premium-trust-badge" className="flex items-center gap-1.5 text-kora-sage">
              <Lock size={12} />
              <span className="text-[11px] font-semibold">Paiement 100% sécurisé</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Premium;
